<template>
  <div class="page-container">
    <div class="page-title-container flex items-center">
      <wz-button variant="light" class="mr-3" @click="$router.push({ name: 'medical-leave-list' })">
        <i class="ri-arrow-left-line"></i> Volver
      </wz-button>
      <h1 class="page-title mb-0">Detalle de descanso médico</h1>
      <wz-badge v-if="leave" :variant="getStatusColor(leave.status)" pill class="ml-3">{{ leave.status }}</wz-badge>
    </div>

    <div v-if="leave">
      <!-- Card: datos del colaborador -->
      <wz-card class="mb-4">
        <template #header>
          <div class="wz-card-title">Datos del colaborador</div>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Nombre</div>
              <div class="detail-value font-bold">{{ leave.employeeName }}</div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Empresa</div>
              <div class="detail-value">{{ leave.companyName }}</div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Área</div>
              <div class="detail-value">{{ leave.areaName }}</div>
            </div>
          </div>
          <div class="flex flex-wrap mt-3 -mx-2">
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Responsable de RRHH</div>
              <div class="detail-value">{{ leave.hrResponsibleEmail || 'No asignado' }}</div>
            </div>
          </div>
      </wz-card>

      <!-- Card: datos del descanso -->
      <wz-card class="mb-4">
        <template #header-title>Datos del descanso</template>
        <template #header-actions>
          <wz-badge :variant="getStatusColor(leave.status)" pill>{{ leave.status }}</wz-badge>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Tipo</div>
              <div class="detail-value">
                <wz-badge :variant="getTypeColor(leave.leaveType)" pill>{{ getTypeLabel(leave.leaveType) }}</wz-badge>
              </div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Fecha inicio</div>
              <div class="detail-value" v-local-date="leave.startDate"></div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Fecha fin</div>
              <div class="detail-value" v-local-date="leave.endDate"></div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Días totales</div>
              <div class="detail-value font-bold">{{ leave.calendarDays }}</div>
            </div>
          </div>
          <div class="flex flex-wrap mt-3 -mx-2">
            <!-- Ocultos por PM: el desglose correcto está en "Indicadores de subsidio" -->
            <div class="w-full md:w-1/4 px-2" v-if="false">
              <div class="detail-label text-muted text-sm">Días empleador</div>
              <div class="detail-value">{{ leave.employerDays }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2" v-if="false">
              <div class="detail-label text-muted text-sm">Días EsSalud</div>
              <div class="detail-value">{{ leave.essaludDays }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2" v-if="false">
              <div class="detail-label text-muted text-sm">Días subsidiados</div>
              <div class="detail-value font-bold text-primary">{{ leave.subsidizedDays }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Edad del colaborador</div>
              <div class="detail-value">{{ leave.employeeAge != null ? leave.employeeAge + ' años' : '---' }}</div>
            </div>
          </div>
          <div class="flex flex-wrap mt-3 -mx-2" v-if="leave.cancellationReason">
            <div class="w-full px-2">
              <div class="detail-label text-muted text-sm">Motivo de cancelación</div>
              <div class="detail-value text-danger">{{ leave.cancellationReason }}</div>
            </div>
          </div>
      </wz-card>

      <!-- Card: indicadores de subsidio (DM5 acumulado + DM6 maternidad) -->
      <wz-card class="mb-4">
        <template #header>
          <div class="wz-card-title">Indicadores de subsidio</div>
        </template>

        <!-- Leyenda cuando el DM está cancelado: el acumulado del año da 0 (no aporta), pero el desglose
             conserva los valores históricos que tenía antes de anularse. -->
        <wz-alert v-if="leave.status === 'CANCELADO'" variant="info" icon="ri-information-line" class="mb-3">
          Este descanso está cancelado y NO aporta a los indicadores del año. Los datos que se muestran a
          continuación son los que tenía el descanso antes de cancelarse (histórico).
        </wz-alert>

        <!-- CRUCE DE AÑOS: dos columnas por año calendario -->
        <div class="subsidy-block" v-if="leave.crossYearData">
          <div class="text-muted text-sm mb-3">
            <i class="ri-information-line mr-1"></i>
            Este descanso cruza dos años. Datos por año calendario:
          </div>
          <div class="flex flex-wrap -mx-2">
            <!-- Columna izquierda: año de inicio -->
            <div class="w-full md:w-1/2 px-2 mb-3">
              <div class="year-col">
                <div class="subsidy-block-title">{{ leaveStartYear }}</div>
                <div class="detail-label text-muted text-sm">Días de descanso acumulados (año calendario)</div>
                <div class="detail-value font-bold text-lg">{{ leave.accumulatedDays12Months }} días</div>
                <wz-badge v-if="leave.totalSubsidyDaysYear > 0" variant="warning-solid" pill class="mt-2">
                  {{ subsidyRegime.text }}
                </wz-badge>
                <div v-else class="text-muted text-sm mt-2">
                  Aún no supera los 20 días acumulados en el año (subsidio EsSalud no aplica).
                </div>
                <div class="flex flex-wrap -mx-2 mt-3">
                  <div class="w-full md:w-1/2 px-2 mb-2">
                    <div class="detail-label text-muted text-sm">Días a cargo del empleador</div>
                    <div class="detail-value font-bold">{{ startYearEmployerDays }}</div>
                  </div>
                  <div class="w-full md:w-1/2 px-2 mb-2">
                    <div class="detail-label text-muted text-sm">Días de subsidio EsSalud</div>
                    <div class="detail-value font-bold text-primary">{{ startYearEssaludDays }}</div>
                  </div>
                  <div class="w-full px-2">
                    <div class="detail-label text-muted text-sm">Total días subsidiados en {{ leaveStartYear }}</div>
                    <div class="detail-value font-bold text-primary">{{ leave.totalSubsidyDaysYear }}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Columna derecha: año de fin -->
            <div class="w-full md:w-1/2 px-2 mb-3">
              <div class="year-col">
                <div class="subsidy-block-title">{{ leave.crossYearData.year }}</div>
                <div class="detail-label text-muted text-sm">Días de descanso acumulados (año calendario)</div>
                <div class="detail-value font-bold text-lg">{{ leave.crossYearData.accumulatedDaysInYear }} días</div>
                <wz-badge v-if="leave.crossYearData.totalSubsidyDaysInYear > 0" variant="warning-solid" pill class="mt-2">
                  {{ subsidyRegime.text }}
                </wz-badge>
                <div v-else class="text-muted text-sm mt-2">
                  Aún no supera los 20 días acumulados en el año (subsidio EsSalud no aplica).
                </div>
                <div class="flex flex-wrap -mx-2 mt-3">
                  <div class="w-full md:w-1/2 px-2 mb-2">
                    <div class="detail-label text-muted text-sm">Días a cargo del empleador</div>
                    <div class="detail-value font-bold">{{ leave.crossYearData.employerDaysInThisYear }}</div>
                  </div>
                  <div class="w-full md:w-1/2 px-2 mb-2">
                    <div class="detail-label text-muted text-sm">Días de subsidio EsSalud</div>
                    <div class="detail-value font-bold text-primary">{{ leave.crossYearData.essaludDaysInThisYear }}</div>
                  </div>
                  <div class="w-full px-2">
                    <div class="detail-label text-muted text-sm">Total días subsidiados en {{ leave.crossYearData.year }}</div>
                    <div class="detail-value font-bold text-primary">{{ leave.crossYearData.totalSubsidyDaysInYear }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- UN SOLO AÑO: render como hoy (bloques Acumulado + Desglose) -->
        <template v-else>
          <!-- BLOQUE 1 — Acumulado del año -->
          <div class="subsidy-block" v-if="leave.accumulatedDays12Months != null">
            <div class="subsidy-block-title">Acumulado del año</div>
            <div class="detail-label text-muted text-sm">Días de descanso acumulados (año calendario)</div>
            <div class="detail-value font-bold text-lg">{{ leave.accumulatedDays12Months }} días</div>
            <wz-badge v-if="subsidyRegime.fromDay1 || leave.appliesEssaludSubsidy" variant="warning-solid" pill class="mt-2">
              {{ subsidyRegime.text }}
            </wz-badge>
            <div v-else class="text-muted text-sm mt-2">
              Aún no supera los 20 días acumulados en el año (subsidio EsSalud no aplica).
            </div>
          </div>

          <!-- BLOQUE 2 — Desglose de este descanso -->
          <div class="subsidy-block" v-if="leave.subsidyEmployerDays != null">
            <div class="subsidy-block-title">Desglose de este descanso</div>
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2 mb-2">
                <div class="detail-label text-muted text-sm">Días a cargo del empleador</div>
                <div class="detail-value font-bold text-lg">{{ leave.subsidyEmployerDays }}</div>
              </div>
              <div class="w-full md:w-1/3 px-2 mb-2">
                <div class="detail-label text-muted text-sm">Días de subsidio EsSalud</div>
                <div class="detail-value font-bold text-lg text-primary">{{ leave.subsidyDays }}</div>
              </div>
              <div class="w-full md:w-1/3 px-2 mb-2" v-if="leave.totalSubsidyDaysYear != null">
                <div class="detail-label text-muted text-sm">Total días subsidiados en el año</div>
                <div class="detail-value font-bold text-lg text-primary">{{ leave.totalSubsidyDaysYear }}</div>
              </div>
            </div>
          </div>
        </template>

        <!-- BLOQUE 3 — Subsidio por maternidad (solo si aplica) -->
        <div class="subsidy-block" v-if="leave.leaveType === 'MATERNIDAD'">
          <div class="subsidy-block-title">Subsidio por maternidad <span class="text-muted">(orientativo)</span></div>
          <wz-badge :variant="leave.maternitySubsidyEligible ? 'success-solid' : 'warning-solid'" pill>
            {{ leave.maternitySubsidyEligible ? 'Posible subsidio por maternidad' : 'Verificar elegibilidad con EsSalud' }}
          </wz-badge>
          <div class="text-muted text-sm mt-2">
            <template v-if="leave.maternitySubsidyEligible">
              La colaboradora tiene más de 3 meses de contrato. Posible subsidio por maternidad.
            </template>
            <template v-else>
              La colaboradora tiene menos de 3 meses de contrato. Verificar elegibilidad con EsSalud.
            </template>
          </div>
        </div>
      </wz-card>

      <!-- Card: datos médicos (v-if tipo != PATERNIDAD) -->
      <wz-card v-if="leave.leaveType !== 'PATERNIDAD'" class="mb-4">
        <template #header>
          <div class="wz-card-title">Datos médicos</div>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Tipo de atención</div>
              <div class="detail-value">{{ leave.careType ? getCareTypeLabel(leave.careType) : '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">N° CITT</div>
              <div class="detail-value">{{ leave.cittNumber || '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Fecha de canje CITT</div>
              <div class="detail-value" v-if="leave.cittExchangeDate" v-local-date="leave.cittExchangeDate"></div>
              <div class="detail-value" v-else>---</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Institución médica</div>
              <div class="detail-value">{{ leave.medicalInstitution || '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Doctor</div>
              <div class="detail-value">{{ leave.doctorName || '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Especialidad</div>
              <div class="detail-value">{{ leave.doctorSpecialty || '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">N° Colegio Médico</div>
              <div class="detail-value">{{ leave.medicalCollegeNumber || '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Centro médico</div>
              <div class="detail-value">{{ leave.medicalCenter || '---' }}</div>
            </div>
          </div>
          <!-- Diagnóstico (confidencial) -->
          <div class="flex flex-wrap mt-3 -mx-2">
            <div class="w-full px-2">
              <div class="detail-label text-muted text-sm">
                <i class="ri-lock-line mr-1"></i> Diagnóstico — Información confidencial
              </div>
              <div class="detail-value">{{ leave.diagnosis || '---' }}</div>
            </div>
          </div>
          <!-- Certificado médico (atención Particular) -->
          <div class="flex flex-wrap mt-3 -mx-2" v-if="leave.careType === 'PARTICULAR' || leave.medicalCertificateName">
            <div class="w-full px-2">
              <div class="detail-label text-muted text-sm">Certificado médico</div>
              <div class="detail-value flex items-center gap-2 flex-wrap">
                <template v-if="leave.medicalCertificateName">
                  <span>{{ leave.medicalCertificateName }}</span>
                  <wz-button variant="light" size="sm" @click="downloadCertificate">
                    <i class="ri-download-2-line mr-1"></i>Descargar
                  </wz-button>
                </template>
                <span v-else class="text-muted">No adjuntado</span>
                <wz-button variant="outline" size="sm" @click="triggerCertificatePicker">
                  <i class="ri-upload-2-line mr-1"></i>{{ leave.medicalCertificateName ? 'Reemplazar' : 'Adjuntar' }}
                </wz-button>
              </div>
              <input
                ref="certInput"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style="display: none"
                @change="onCertificateSelected"
              />
            </div>
          </div>
      </wz-card>

      <!-- Card: subsidio y trámite (v-if caseStatus = SUBSIDIO) -->
      <wz-card v-if="leave.caseStatus === 'SUBSIDIO'" class="mb-4">
        <template #header>
          <div class="wz-card-title">Subsidio y trámite EsSalud</div>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Estado del caso</div>
              <div class="detail-value">
                <wz-badge :variant="getCaseStatusColor(leave.caseStatus)" pill>
                  {{ getCaseStatusLabel(leave.caseStatus) }}
                </wz-badge>
              </div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Tipo de seguro</div>
              <div class="detail-value">{{ leave.insuranceType ? getInsuranceLabel(leave.insuranceType) : '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Estado del trámite</div>
              <div class="detail-value">
                <wz-badge v-if="leave.processStatus" :variant="getProcessStatusColor(leave.processStatus)" pill>
                  {{ getProcessStatusLabel(leave.processStatus) }}
                </wz-badge>
                <span v-else>---</span>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap mt-3 -mx-2">
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Pago por día</div>
              <div class="detail-value font-bold">{{ leave.dailyPayment != null ? 'S/ ' + Number(leave.dailyPayment).toFixed(2) : '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Monto pagado</div>
              <div class="detail-value font-bold text-primary">{{ leave.amountPaid != null ? 'S/ ' + Number(leave.amountPaid).toFixed(2) : '---' }}</div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Monto cobrado</div>
              <div class="detail-value font-bold text-success">
                <template v-if="leave.amountCollected != null">
                  S/ {{ Number(leave.amountCollected).toFixed(2) }}
                  <span v-if="leave.collectedAt" class="text-muted text-sm font-normal">
                    (registrado el {{ leave.collectedAt.split('-').reverse().join('/') }})
                  </span>
                </template>
                <template v-else>---</template>
              </div>
            </div>
          </div>
      </wz-card>

      <!-- Card: gestión -->
      <wz-card class="mb-4">
        <template #header>
          <div class="wz-card-title">Gestión</div>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Encargado</div>
              <div class="detail-value">{{ leave.personInCharge || '---' }}</div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Documento adjunto</div>
              <div class="detail-value flex items-center" v-if="leave.attachmentName">
                <i class="ri-file-line mr-2 text-primary"></i>
                <span>{{ leave.attachmentName }}</span>
              </div>
              <div class="detail-value" v-else>---</div>
            </div>
          </div>
          <div class="flex flex-wrap mt-3 -mx-2">
            <div class="w-full px-2">
              <div class="detail-label text-muted text-sm">Observaciones</div>
              <div class="detail-value">{{ leave.observations || '---' }}</div>
            </div>
          </div>
      </wz-card>

      <!-- Botones de acción -->
      <div v-if="leave.status === 'ACTIVO'" class="flex justify-end mt-3">
        <wz-button
          variant="primary"
          class="mr-2"
          @click="openFinalize()"
        >
          <i class="ri-check-line mr-1"></i> Finalizar
        </wz-button>
        <wz-button
          variant="light"
          @click="openCancel()"
        >
          <i class="ri-forbid-line mr-1"></i> Cancelar
        </wz-button>
        <wz-button
          v-if="leave && leave.status !== 'CANCELADO'"
          variant="danger"
          class="ml-2"
          @click="showAnnul = true"
        >
          <i class="ri-close-circle-line mr-1"></i> Anular por error
        </wz-button>
      </div>

      <!-- DM FINALIZADO con subsidio: registrar/editar el cobro EsSalud (llega semanas/meses después). -->
      <div v-if="leave.status === 'FINALIZADO' && leave.essaludDays > 0" class="flex justify-end mt-3">
        <wz-button variant="primary" @click="openCollected()">
          <i class="ri-money-dollar-circle-line mr-1"></i>
          {{ leave.amountCollected != null ? 'Editar cobro EsSalud' : 'Registrar cobro EsSalud' }}
        </wz-button>
      </div>
    </div>

    <!-- Modal: Finalizar -->
    <wz-modal v-model="modalFinalize" title="Finalizar descanso médico">
      <div class="mb-3">
        <label>Fecha real de fin <span class="text-danger">*</span></label>
        <wz-datepicker v-model="finalizeDate" />
      </div>
      <div v-if="leave && finalizeDate" class="text-muted text-sm mt-2">
        Días recalculados: {{ computeFinalizeDays() }}
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalFinalize = false">Volver</wz-button>
        <wz-button :disabled="!finalizeDate" @click="onFinalize">Finalizar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Registrar cobro EsSalud (DM FINALIZADO con subsidio) -->
    <wz-modal v-model="modalCollected" title="Registrar cobro EsSalud">
      <div class="flex flex-wrap -mx-2 mb-3" v-if="leave">
        <div class="w-full md:w-1/2 px-2">
          <div class="detail-label text-muted text-sm">Días de subsidio EsSalud</div>
          <div class="detail-value font-bold">{{ leave.essaludDays }}</div>
        </div>
        <div class="w-full md:w-1/2 px-2" v-if="leave.amountPaid != null">
          <div class="detail-label text-muted text-sm">Monto pagado estimado</div>
          <div class="detail-value font-bold text-primary">S/ {{ Number(leave.amountPaid).toFixed(2) }}</div>
        </div>
      </div>
      <div class="mb-3">
        <wz-input
          label="Monto cobrado real (S/) *"
          type="number"
          step="0.01"
          min="0"
          v-model.number="collectedAmount"
          placeholder="Monto reembolsado por EsSalud"
        />
      </div>
      <div class="mb-3">
        <label>Fecha de cobro (opcional)</label>
        <wz-datepicker v-model="collectedDate" />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalCollected = false">Volver</wz-button>
        <wz-button :disabled="!collectedAmount || collectedAmount <= 0" @click="onCollected">Guardar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Cancelar -->
    <wz-modal v-model="modalCancel" title="Cancelar descanso médico">
      <div class="mb-3">
        <label>Motivo de cancelación <span class="text-danger">*</span></label>
        <wz-textarea
          v-model="cancelReason"
          rows="3"
          placeholder="Ingrese el motivo de la cancelación"
        />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalCancel = false">Volver</wz-button>
        <wz-button variant="danger" :disabled="!cancelReason.trim()" @click="onCancel">Cancelar descanso</wz-button>
      </template>
    </wz-modal>

    <annul-by-mistake-modal
      v-model="showAnnul"
      :submitting="annulSubmitting"
      :entity-description="leave ? `${leave.employeeName || ''} — DM del ${leave.startDate} al ${leave.endDate}` : ''"
      @confirm="doAnnul"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { MedicalLeaveService } from '@/core/services'
import { Constants } from '@/core/utils'
import { isFullSubsidyNoEmployer } from '@/core/utils/subsidyRegime'
import AnnulByMistakeModal from '@/core/components/AnnulByMistakeModal.vue'

export default {
  name: 'MedicalLeaveDetail',
  components: { AnnulByMistakeModal },
  data() {
    return {
      leave: null,
      finalizeDate: null,
      cancelReason: '',
      modalFinalize: false,
      modalCancel: false,
      modalCollected: false,
      collectedAmount: null,
      collectedDate: null,
      showAnnul: false,
      annulSubmitting: false,
    }
  },
  computed: {
    // Año de inicio del DM (columna izquierda del desglose por año).
    leaveStartYear() {
      return this.leave && this.leave.startDate ? Number(this.leave.startDate.slice(0, 4)) : null
    },
    // Porción del año de inicio = total del DM menos la porción del año de fin (crossYearData).
    startYearEmployerDays() {
      if (!this.leave || !this.leave.crossYearData) return this.leave ? this.leave.subsidyEmployerDays : 0
      return (this.leave.subsidyEmployerDays || 0) - (this.leave.crossYearData.employerDaysInThisYear || 0)
    },
    startYearEssaludDays() {
      if (!this.leave || !this.leave.crossYearData) return this.leave ? this.leave.subsidyDays : 0
      return (this.leave.subsidyDays || 0) - (this.leave.crossYearData.essaludDaysInThisYear || 0)
    },
    // Mensaje del badge de subsidio según el régimen legal del DM (tipo + cobertura SCTR).
    //  - fromDay1: regímenes que subsidian desde el día 1 (Maternidad Ley 26644 / Accidente laboral con SCTR
    //    Ley 26790) → el badge se muestra siempre, sin depender del acumulado de 20 días.
    //  - común (enfermedad, accidente común, accidente laboral SIN SCTR, o tipo desconocido) → primeros 20
    //    días a cargo del empleador; el badge sigue condicionado al acumulado > 20.
    subsidyRegime() {
      const type = this.leave ? this.leave.leaveType : null
      const hasSctr = !!(this.leave && this.leave.hasSctrHealth === true)
      // Decisión centralizada en el helper (mismo criterio que el backend).
      if (isFullSubsidyNoEmployer(type, hasSctr)) {
        if (type === 'MATERNIDAD') {
          return { fromDay1: true, text: 'Subsidio EsSalud desde el día 1 — Ley 26644 (98 días)' }
        }
        return { fromDay1: true, text: 'Subsidio SCTR desde el día 1 — Ley 26790' }
      }
      return { fromDay1: false, text: 'Aplica subsidio EsSalud — primeros 20 días a cargo del empleador, día 21+ EsSalud' }
    },
  },
  methods: {
    getTypeColor(leaveType) {
      const found = Object.values(Constants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.color : 'dark'
    },
    getTypeLabel(leaveType) {
      const found = Object.values(Constants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.label : leaveType
    },
    getStatusColor(status) {
      const found = Object.values(Constants.MedicalLeaveStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    getCaseStatusColor(caseStatus) {
      const found = Object.values(Constants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.color : 'dark'
    },
    getCaseStatusLabel(caseStatus) {
      const found = Object.values(Constants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.label : caseStatus
    },
    getProcessStatusColor(processStatus) {
      const found = Object.values(Constants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.color : 'dark'
    },
    getProcessStatusLabel(processStatus) {
      const found = Object.values(Constants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.label : processStatus
    },
    getInsuranceLabel(insuranceType) {
      const found = Object.values(Constants.MedicalLeaveInsuranceType).find(s => s.key === insuranceType)
      return found ? found.label : insuranceType
    },
    getCareTypeLabel(careType) {
      const found = Object.values(Constants.MedicalLeaveCareType).find(s => s.key === careType)
      return found ? found.label : careType
    },
    openFinalize() {
      this.finalizeDate = null
      this.modalFinalize = true
    },
    openCancel() {
      this.cancelReason = ''
      this.modalCancel = true
    },
    async onFinalize() {
      this.modalFinalize = false
      await this.confirmFinalize()
    },
    async onCancel() {
      this.modalCancel = false
      await this.confirmCancel()
    },
    computeFinalizeDays() {
      if (!this.leave || !this.finalizeDate) return '-'
      const start = new Date(this.leave.startDate + 'T00:00:00')
      const end   = new Date(this.finalizeDate    + 'T00:00:00')
      return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    },
    async confirmFinalize() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.finalize(this.leave.id, { actualEndDate: this.finalizeDate })
        Alert.success('Descanso médico finalizado correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al finalizar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.cancel(this.leave.id, { cancellationReason: this.cancelReason })
        Alert.success('Descanso médico cancelado')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doAnnul(reason) {
      try {
        this.annulSubmitting = true
        await MedicalLeaveService.annulMedicalLeave(this.leave.id, reason)
        Alert.success('Descanso médico anulado por error')
        this.showAnnul = false
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular el descanso médico')
      } finally {
        this.annulSubmitting = false
      }
    },
    openCollected() {
      // Precarga el valor ya registrado (si existe) para "Editar cobro".
      this.collectedAmount = this.leave.amountCollected != null ? this.leave.amountCollected : null
      this.collectedDate   = this.leave.collectedAt || null
      this.modalCollected  = true
    },
    async onCollected() {
      this.modalCollected = false
      await this.confirmCollected()
    },
    async confirmCollected() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.registerCollected(this.leave.id, {
          amountCollected: this.collectedAmount,
          collectedAt: this.collectedDate || null,
        })
        Alert.success('Cobro EsSalud registrado correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al registrar el cobro')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.get(this.$route.params.id)
        this.leave = resp.payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el descanso médico')
        this.$router.push({ name: 'medical-leave-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    // ---- Certificado médico ----
    triggerCertificatePicker() {
      if (this.leave.medicalCertificateName && !confirm('¿Desea reemplazar el certificado médico actual?')) return
      this.$refs.certInput.click()
    },
    async onCertificateSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 10MB')
        this.$refs.certInput.value = ''
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.uploadCertificate(this.leave.id, file)
        Alert.success('Certificado médico adjuntado correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir el certificado médico')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.$refs.certInput.value = ''
      }
    },
    async downloadCertificate() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.downloadCertificate(this.leave.id)
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.leave.medicalCertificateName || 'certificado-medico')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el certificado médico')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  created() {
    this.loadData()
  },
}
</script>

<style scoped>
/* Bloques de "Indicadores de subsidio": separación visual y jerarquía clara */
.subsidy-block {
  padding: 16px 0;
}
.subsidy-block:first-of-type {
  padding-top: 0;
}
.subsidy-block:last-of-type {
  padding-bottom: 0;
}
.subsidy-block + .subsidy-block {
  border-top: 1px solid var(--admin-border-color);
}
.subsidy-block-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--admin-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}
.text-lg {
  font-size: 18px;
}
/* Columnas por año cuando el DM cruza dos años calendario */
.year-col {
  border: 1px solid var(--admin-border-color);
  border-radius: 8px;
  padding: 14px 16px;
  height: 100%;
}
.year-col .subsidy-block-title {
  color: var(--admin-text-primary, inherit);
  margin-bottom: 8px;
}
</style>
